import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRefFromPath } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

import CountryFlag from "../../components/common/CountryFlag";
import { db, storage } from "../../firebase";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";

const functions = getFunctions(undefined, "us-central1");

/** Matches submitVerificationRequest (`provisionally_approved`) and any `pending` rows */
const QUEUE_STATUS_FILTER = ["pending", "provisionally_approved"];

function tsMs(value) {
  if (!value) return 0;
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (typeof value === "object" && value.seconds != null) {
    return Number(value.seconds) * 1000;
  }
  return 0;
}

function formatSubmitted(value) {
  if (!value) return "—";
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function shorten(value, start = 10, end = 8) {
  const str = String(value || "");
  if (str.length <= start + end + 3) return str || "—";
  return `${str.slice(0, start)}…${str.slice(-end)}`;
}

function callableErrorMessage(error) {
  const code = error?.code ? String(error.code) : "";
  const msg = error?.message ? String(error.message) : "";
  if (code && msg) return `${code}: ${msg}`;
  return msg || "Request failed.";
}

function badgeClassForStatus(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "approved") return "admin1-vq__badge admin1-vq__badge--approved";
  if (s === "rejected") return "admin1-vq__badge admin1-vq__badge--rejected";
  if (s === "pending") return "admin1-vq__badge admin1-vq__badge--pending";
  if (s === "provisionally_approved") {
    return "admin1-vq__badge admin1-vq__badge--provisional";
  }
  return "admin1-vq__badge admin1-vq__badge--muted";
}

function FirebaseStorageImagePreview({ storagePath, t }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    const path = String(storagePath || "").trim();
    if (!path) return undefined;

    (async () => {
      try {
        const resolved = await getDownloadURL(storageRefFromPath(storage, path));
        if (!cancelled) setUrl(resolved);
      } catch (e) {
        console.error("VerificationQueue image preview:", path, e);
        if (!cancelled) setUrl("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  if (!storagePath) {
    return <span className="admin1-vq__muted">{t("admin.verification.no_face_photo_path", { defaultValue: "No face photo path" })}</span>;
  }
  if (!url) return <span className="admin1-vq__muted">{t("admin.verification.loading_image", { defaultValue: "Loading image..." })}</span>;
  return (
    <img src={url} alt="" className="admin1-vq__media-img" />
  );
}

function FirebaseStorageVideoPreview({ storagePath }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    const path = String(storagePath || "").trim();
    if (!path) return undefined;

    (async () => {
      try {
        const resolved = await getDownloadURL(storageRefFromPath(storage, path));
        if (!cancelled) setUrl(resolved);
      } catch (e) {
        console.error("VerificationQueue video preview:", path, e);
        if (!cancelled) setUrl("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  if (!storagePath) return null;
  if (!url) return <span className="admin1-vq__muted">Loading video…</span>;
  return (
    <video src={url} controls className="admin1-vq__media-video" playsInline />
  );
}

function pickUserDisplayName(user) {
  const n = user?.displayName || user?.name || "";
  const s = typeof n === "string" ? n.trim() : "";
  return s || "—";
}

function pickUserPhotoRaw(user) {
  const raw = user?.photoURL || user?.profileImage || "";
  return typeof raw === "string" ? raw.trim() : "";
}

function UserPhotoThumb({ raw, gender }) {
  const fb = defaultAvatarByGender(gender);
  const [src, setSrc] = useState(fb);

  useEffect(() => {
    let cancelled = false;
    const fallback = defaultAvatarByGender(gender);
    const v = String(raw || "").trim();
    if (!v) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    if (/^https?:\/\//i.test(v)) {
      setSrc(v);
      return () => {
        cancelled = true;
      };
    }
    let path = v;
    if (v.startsWith("gs://")) {
      const m = v.match(/^gs:\/\/[^/]+\/(.+)$/);
      path = m ? m[1] : "";
    }
    if (!path) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const url = await getDownloadURL(storageRefFromPath(storage, path));
        if (!cancelled) setSrc(url);
      } catch {
        if (!cancelled) setSrc(fallback);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [raw, gender]);

  return (
    <img
      src={src}
      alt=""
      className="admin1-vq__user-thumb"
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = defaultAvatarByGender(gender);
      }}
    />
  );
}

export default function VerificationQueuePage() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [userByUid, setUserByUid] = useState({});
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [reasonById, setReasonById] = useState({});
  const [feedbackById, setFeedbackById] = useState({});

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, "verificationRequests"),
      where("status", "in", QUEUE_STATUS_FILTER)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        list.sort(
          (a, b) =>
            tsMs(b.submittedAt || b.createdAt) -
            tsMs(a.submittedAt || a.createdAt)
        );
        setRequests(list);
        setLoading(false);
      },
      (err) => {
        console.error("VerificationQueuePage snapshot error:", err);
        setRequests([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const uids = [...new Set(requests.map((r) => r.uid).filter(Boolean))];
    if (!uids.length) {
      setUserByUid({});
      return undefined;
    }

    (async () => {
      const pairs = await Promise.all(
        uids.map(async (uid) => {
          const snap = await getDoc(doc(db, "users", uid));
          return [uid, snap.exists() ? snap.data() || {} : {}];
        })
      );
      if (!cancelled) setUserByUid(Object.fromEntries(pairs));
    })();

    return () => {
      cancelled = true;
    };
  }, [requests]);

  const reviewFn = useMemo(
    () => httpsCallable(functions, "reviewVerificationRequest"),
    []
  );

  async function handleApprove(item) {
    setWorkingId(item.id);
    setFeedbackById((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    try {
      await reviewFn({
        requestId: item.id,
        action: "approve",
      });
    } catch (error) {
      console.error("reviewVerificationRequest approve:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [item.id]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  async function handleReject(item) {
    const reason = String(reasonById[item.id] || "").trim();
    if (!reason) {
      setFeedbackById((prev) => ({
        ...prev,
        [item.id]: "Enter a rejection reason.",
      }));
      return;
    }

    setWorkingId(item.id);
    setFeedbackById((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });

    try {
      await reviewFn({
        requestId: item.id,
        action: "reject",
        reason,
      });
      setReasonById((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    } catch (error) {
      console.error("reviewVerificationRequest reject:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [item.id]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  const busy = workingId != null;

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.verification.queue_title", { defaultValue: "Verification queue" })}</h1>
        <p className="admin1-dashboard__subtitle">
          {t("admin.verification.queue_subtitle_open_requests", { defaultValue: "Open requests:" })}{" "}
          <span className="admin1-users__kbd">
            status in [&quot;pending&quot;, &quot;provisionally_approved&quot;]
          </span>{" "}
          {t("admin.verification.queue_subtitle_matches_flow", { defaultValue: "(matches submit flow). Approve or reject via" })}{" "}
          <span className="admin1-users__kbd">reviewVerificationRequest</span>.
        </p>
      </header>

      {loading ? (
        <div className="admin1-state-card">{t("admin.verification.loading_queue", { defaultValue: "Loading queue..." })}</div>
      ) : requests.length === 0 ? (
        <div className="admin1-state-card">
          {t("admin.verification.no_queue_requests", { defaultValue: "No verification requests in queue (pending or provisionally approved)." })}
        </div>
      ) : (
        <section className="admin1-vq__grid">
          {requests.map((item) => {
            const user = userByUid[item.uid] || {};
            const displayName = pickUserDisplayName(user);
            const photoRaw = pickUserPhotoRaw(user);
            const countryRaw = String(
              user.displayCountry || user.realCountry || user.country || ""
            ).trim();
            const gender = String(user.gender ?? "—");
            const genderForAvatar = String(user.gender || "");
            const submitted = item.submittedAt || item.createdAt;
            const videoPath = String(item.verificationVideoPath || "").trim();
            const statusLabel = String(item.status ?? "—");
            const userVerification = String(
              user.verificationStatus ?? "—"
            );
            const isWorking = busy && workingId === item.id;
            const feedback = feedbackById[item.id];

            return (
              <article key={item.id} className="admin1-vq__card">
                <div className="admin1-vq__card-head">
                  <div>
                    <div className="admin1-vq__card-title">
                      Request{" "}
                      <span className="admin1-vq__mono">{shorten(item.id)}</span>
                    </div>
                    <div className="admin1-vq__mono admin1-vq__mono--full">
                      {item.id}
                    </div>
                  </div>
                  <span className={badgeClassForStatus(statusLabel)}>
                    {statusLabel}
                  </span>
                </div>

                <div className="admin1-vq__user-row">
                  <UserPhotoThumb raw={photoRaw} gender={genderForAvatar} />
                  <div className="admin1-vq__user-meta">
                    <div className="admin1-vq__strong">{displayName}</div>
                    <div className="admin1-vq__muted">
                      uid:{" "}
                      <span className="admin1-vq__mono">{item.uid}</span>
                    </div>
                    <div className="admin1-vq__tags">
                      <span className="admin1-vq__tag">{gender}</span>
                      <span className="admin1-vq__tag admin1-vq__tag--country">
                        <CountryFlag countryCode={countryRaw} />
                      </span>
                    </div>
                  </div>
                </div>

                <dl className="admin1-vq__dl">
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.verification.submitted_at", { defaultValue: "submittedAt" })}</dt>
                    <dd>{formatSubmitted(submitted)}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.verification.request_status", { defaultValue: "verification (request)" })}</dt>
                    <dd>{statusLabel}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.verification.user_status", { defaultValue: "verification (user)" })}</dt>
                    <dd>{userVerification}</dd>
                  </div>
                </dl>

                <div className="admin1-vq__media-block">
                  <div className="admin1-vq__media-label">{t("admin.verification.face_photo", { defaultValue: "Face photo" })}</div>
                  <FirebaseStorageImagePreview
                    storagePath={item.profilePhotoPath}
                    t={t}
                  />
                </div>

                {videoPath ? (
                  <div className="admin1-vq__media-block">
                    <div className="admin1-vq__media-label">{t("admin.verification.video", { defaultValue: "Video" })}</div>
                    <FirebaseStorageVideoPreview
                      storagePath={item.verificationVideoPath}
                    />
                  </div>
                ) : (
                  <div className="admin1-vq__muted admin1-vq__skip">
                    No verification video on this request.
                  </div>
                )}

                <label className="admin1-vq__reject-label">
                  Rejection reason (required to reject)
                  <textarea
                    className="admin1-vq__textarea"
                    rows={2}
                    placeholder={t("admin.verification.review_reason_placeholder", { defaultValue: "Reason shown to ops / stored on review" })}
                    value={reasonById[item.id] || ""}
                    onChange={(e) =>
                      setReasonById((prev) => ({
                        ...prev,
                        [item.id]: e.target.value,
                      }))
                    }
                  />
                </label>

                <div className="admin1-vq__actions">
                  <button
                    type="button"
                    className="admin1-vq__btn admin1-vq__btn--approve"
                    disabled={busy}
                    onClick={() => handleApprove(item)}
                  >
                    {isWorking ? "…" : "Approve"}
                  </button>
                  <button
                    type="button"
                    className="admin1-vq__btn admin1-vq__btn--reject"
                    disabled={busy}
                    onClick={() => handleReject(item)}
                  >
                    {isWorking ? "…" : "Reject"}
                  </button>
                </div>

                {feedback ? (
                  <div className="admin1-vq__feedback">{feedback}</div>
                ) : null}
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}
